/**
 * BaseRegistry — generic registry foundation shared by the engine's many
 * type registries (component types, visual-script nodes, VFX nodes, editor
 * tools/windows, etc.).
 *
 * Historically each registry re-implemented the same `Map<id, def>` storage
 * plus category and keyword indexes plus a scored `search()`. This base
 * captures that pattern once; concrete registries extend it and add only
 * their domain-specific lookups (e.g. NodeRegistry's `getEventNodes()` or
 * `resolvePort()`).
 *
 * Design notes:
 * - `register()` overwrites an existing entry with the same id (last wins),
 *   matching the visual-script NodeRegistry. Registries that need
 *   idempotent/skip semantics override `register()`.
 * - The category index stores **ids** (sorted) like NodeRegistry; use
 *   {@link BaseRegistry.getDefsByCategory} when you want the definitions.
 * - Subclasses can contribute extra search keywords via {@link _extraKeywords}.
 *
 * @module @engine/graph/BaseRegistry
 */

/** Minimal shape every registry entry must provide. */
export interface RegistryEntry {
  /** Unique identifier (registry key). */
  readonly id: string;
  /** Human-readable display name (used by search). */
  readonly name: string;
  /** Category bucket for grouping / palette organization. */
  readonly category: string;
  /** Optional extra search keywords. */
  readonly keywords?: readonly string[];
}

/** Public contract implemented by {@link BaseRegistry} and all concrete registries. */
export interface IRegistry<TDef extends RegistryEntry> {
  register(def: TDef): void;
  registerAll(defs: readonly TDef[]): void;
  unregister(id: string): boolean;
  get(id: string): TDef | undefined;
  has(id: string): boolean;
  getAll(): readonly TDef[];
  /** Ids in a category, sorted alphabetically. */
  getByCategory(category: string): readonly string[];
  /** All categories that have at least one entry, sorted. */
  getCategories(): readonly string[];
  /** Scored fuzzy search over name / id / category / keywords. */
  search(query: string, limit?: number): readonly TDef[];
  readonly size: number;
}

export class BaseRegistry<TDef extends RegistryEntry> implements IRegistry<TDef> {
  /** All registered entries keyed by `id`. */
  protected readonly _items = new Map<string, TDef>();
  /** Index: category → sorted entry ids. */
  protected readonly _byCategory = new Map<string, string[]>();
  /** Index: keyword → entry ids. */
  protected readonly _byKeyword = new Map<string, Set<string>>();

  /**
   * How {@link register} treats a duplicate id:
   * - `'replace'` (default): last-wins, overwrites the existing entry.
   * - `'skip'`: idempotent, keeps the first registration (node packs may
   *   import each other and re-register shared definitions).
   * Subclasses override this instead of re-implementing skip logic.
   */
  protected readonly conflictPolicy: 'replace' | 'skip' = 'replace';

  // --------------------------------------------------------------------------
  // Registration
  // --------------------------------------------------------------------------

  /**
   * Register an entry. On a duplicate id, behavior follows {@link conflictPolicy}
   * (`'replace'` overwrites — default; `'skip'` keeps the first).
   */
  register(def: TDef): void {
    if (this._items.has(def.id)) {
      if (this.conflictPolicy === 'skip') return;
      // Replacing: drop the old indexes first so stale category/keyword
      // entries don't linger.
      this._removeFromIndexes(this._items.get(def.id)!);
    }

    this._items.set(def.id, def);

    // Category index (sorted ids).
    let cat = this._byCategory.get(def.category);
    if (!cat) {
      cat = [];
      this._byCategory.set(def.category, cat);
    }
    if (!cat.includes(def.id)) {
      cat.push(def.id);
      cat.sort();
    }

    // Keyword index.
    this._indexKeywords(def);
  }

  /** Register many entries at once. */
  registerAll(defs: readonly TDef[]): void {
    for (const d of defs) this.register(d);
  }

  /** Unregister an entry by id. Returns `false` if it was not registered. */
  unregister(id: string): boolean {
    const def = this._items.get(id);
    if (!def) return false;
    this._items.delete(id);
    this._removeFromIndexes(def);
    return true;
  }

  /** Remove all entries. */
  clear(): void {
    this._items.clear();
    this._byCategory.clear();
    this._byKeyword.clear();
  }

  // --------------------------------------------------------------------------
  // Lookup
  // --------------------------------------------------------------------------

  get(id: string): TDef | undefined {
    return this._items.get(id);
  }

  has(id: string): boolean {
    return this._items.has(id);
  }

  getAll(): readonly TDef[] {
    return [...this._items.values()];
  }

  getByCategory(category: string): readonly string[] {
    return this._byCategory.get(category) ?? [];
  }

  /** Like {@link getByCategory} but resolves to the definitions. */
  getDefsByCategory(category: string): readonly TDef[] {
    return (this._byCategory.get(category) ?? [])
      .map((id) => this._items.get(id)!)
      .filter(Boolean);
  }

  getCategories(): readonly string[] {
    return [...this._byCategory.keys()].sort();
  }

  /** Total number of registered entries. */
  get size(): number {
    return this._items.size;
  }

  /**
   * Scored fuzzy search. Ranking (high → low): exact name, name prefix,
   * name substring, id substring, exact keyword, keyword prefix.
   */
  search(query: string, limit = 50): readonly TDef[] {
    const q = query.toLowerCase().trim();
    if (!q) return [];

    const scored = new Map<string, number>();

    for (const [id, def] of this._items) {
      let score = 0;
      const name = def.name.toLowerCase();
      const entryId = id.toLowerCase();

      if (name === q) score += 100;
      else if (name.startsWith(q)) score += 50;
      else if (name.includes(q)) score += 30;
      else if (entryId.includes(q)) score += 20;
      else {
        const kws = this._byKeyword.get(q);
        if (kws?.has(id)) score += 10;
        else {
          for (const [kw, set] of this._byKeyword) {
            if (kw.startsWith(q) && set.has(id)) {
              score += 5;
              break;
            }
          }
        }
      }

      if (score > 0) scored.set(id, score);
    }

    return [...scored.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([id]) => this._items.get(id)!)
      .filter(Boolean);
  }

  // --------------------------------------------------------------------------
  // Internals
  // --------------------------------------------------------------------------

  /**
   * Override to contribute extra search keywords for an entry (e.g. aliases).
   * Returned strings are lower-cased and indexed alongside the defaults.
   */
  protected _extraKeywords(_def: TDef): readonly string[] {
    return [];
  }

  private _indexKeywords(def: TDef): void {
    const words: string[] = [
      ...def.name.toLowerCase().split(/\s+/),
      ...def.id.toLowerCase().split(/[.\/:]/),
      def.category.toLowerCase(),
      ...(def.keywords ?? []).map((k) => k.toLowerCase()),
      ...this._extraKeywords(def).map((k) => k.toLowerCase()),
    ];

    for (const w of words) {
      if (!w) continue;
      let set = this._byKeyword.get(w);
      if (!set) {
        set = new Set();
        this._byKeyword.set(w, set);
      }
      set.add(def.id);
    }
  }

  private _removeFromIndexes(def: TDef): void {
    const cat = this._byCategory.get(def.category);
    if (cat) {
      const idx = cat.indexOf(def.id);
      if (idx !== -1) cat.splice(idx, 1);
      if (cat.length === 0) this._byCategory.delete(def.category);
    }
    for (const [kw, set] of this._byKeyword) {
      set.delete(def.id);
      if (set.size === 0) this._byKeyword.delete(kw);
    }
  }
}
